import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, AtSign, Ban, Bot, ChevronDown, Clock, Copy, FolderPlus, Gavel, Hash, Link2, Loader2, Lock, LogOut, Megaphone, MessageCircle,
  MessagesSquare, MoreHorizontal, Paperclip, Pencil, Pin, Plus, Reply, ScrollText, Send, Settings, Shield, SmilePlus, Tag, Trash2, UserCog,
  UserMinus, UserPlus, Users, Volume2, X,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import ContextMenu from "@/components/console/ContextMenu";
import DiscordFormatBar from "@/components/DiscordFormatBar";
import { AddBotModal, ChannelModal, ConfirmModal, InviteModal, NickModal, ProfileModal, SanctionModal, ServerSettingsModal } from "@/components/console/ConsoleModals";
import { API } from "@/App";
import { tokenStore } from "@/lib/token";

// Console Discord : le bot se contrôle ici comme depuis un client Discord connecté
// à son compte (serveurs, salons, messages, MP). Le site parle à Discord avec le
// token du bot, qui ne quitte jamais le serveur.

const auth = () => ({ headers: { Authorization: `Bearer ${tokenStore.get()}` } });
const api = {
  get: (path, params) => axios.get(`${API}/dash/bots${path}`, { ...auth(), params }).then((r) => r.data),
  post: (path, body) => axios.post(`${API}/dash/bots${path}`, body || {}, auth()).then((r) => r.data),
  patch: (path, body) => axios.patch(`${API}/dash/bots${path}`, body || {}, auth()).then((r) => r.data),
  del: (path) => axios.delete(`${API}/dash/bots${path}`, auth()).then((r) => r.data),
};
const errorText = (err, fallback) => err?.response?.data?.detail || fallback;
const QUICK_EMOJIS = ["👍", "❤️", "😂", "🎉", "✅", "❌", "👀", "🔥"];
const POLL_MS = 4000;

// Identifiants Discord (snowflakes) comparés sans BigInt : plus long = plus récent.
const idCmp = (a, b) => (a.length - b.length) || (a < b ? -1 : a > b ? 1 : 0);
const byId = (a, b) => idCmp(a.id, b.id);

function when(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const today = new Date();
  const time = d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
  if (d.toDateString() === today.toDateString()) return `Aujourd'hui à ${time}`;
  const y = new Date(today); y.setDate(today.getDate() - 1);
  if (d.toDateString() === y.toDateString()) return `Hier à ${time}`;
  return `${d.toLocaleDateString("fr-FR")} ${time}`;
}

// ── Texte façon Discord : mentions, emojis du serveur, liens, gras, italique, code ──

function Markdown({ text, ctx }) {
  if (!text) return null;
  const blocks = text.split(/```(?:\w+\n)?([\s\S]*?)```/g);
  return blocks.map((part, i) => (i % 2
    ? <pre key={i} className="my-1 rounded bg-black/40 border border-white/5 p-2 text-xs font-mono whitespace-pre-wrap">{part}</pre>
    : <Fragment key={i}>{inline(part, ctx, `b${i}`)}</Fragment>));
}

const TOKEN_RE = /(<@!?\d+>|<@&\d+>|<#\d+>|<a?:\w+:\d+>|@everyone|@here|https?:\/\/[^\s<]+|`[^`\n]+`|\*\*[^*\n]+\*\*|\*[^*\n]+\*|__[^_\n]+__|~~[^~\n]+~~|\|\|[^|\n]+\|\|)/g;

function inline(text, ctx, prefix) {
  return text.split(TOKEN_RE).map((t, i) => {
    const key = `${prefix}-${i}`;
    if (!t) return null;
    let m;
    if ((m = t.match(/^<@!?(\d+)>$/))) return <span key={key} className="px-1 rounded bg-indigo-500/25 text-indigo-200">@{ctx.users[m[1]] || ctx.memberName(m[1]) || "utilisateur"}</span>;
    if ((m = t.match(/^<@&(\d+)>$/))) {
      const role = ctx.roles[m[1]];
      return <span key={key} className="px-1 rounded bg-indigo-500/25" style={{ color: role?.color || "#c7d2fe" }}>@{role?.name || "rôle"}</span>;
    }
    if ((m = t.match(/^<#(\d+)>$/))) return <span key={key} className="px-1 rounded bg-indigo-500/25 text-indigo-200">#{ctx.channelName(m[1]) || "salon"}</span>;
    if ((m = t.match(/^<(a?):(\w+):(\d+)>$/))) return <img key={key} src={`https://cdn.discordapp.com/emojis/${m[3]}.${m[1] ? "gif" : "png"}?size=48`} alt={`:${m[2]}:`} title={`:${m[2]}:`} className="inline w-5 h-5 align-text-bottom" />;
    if (t === "@everyone" || t === "@here") return <span key={key} className="px-1 rounded bg-indigo-500/25 text-indigo-200">{t}</span>;
    if (/^https?:\/\//.test(t)) return <a key={key} href={t} target="_blank" rel="noopener noreferrer" className="text-sky-400 hover:underline break-all">{t}</a>;
    if (/^`[^`]+`$/.test(t)) return <code key={key} className="px-1 rounded bg-black/40 text-[0.85em] font-mono">{t.slice(1, -1)}</code>;
    if (/^\*\*.+\*\*$/.test(t)) return <strong key={key}>{inline(t.slice(2, -2), ctx, key)}</strong>;
    if (/^__.+__$/.test(t)) return <u key={key}>{inline(t.slice(2, -2), ctx, key)}</u>;
    if (/^~~.+~~$/.test(t)) return <s key={key}>{inline(t.slice(2, -2), ctx, key)}</s>;
    if (/^\|\|.+\|\|$/.test(t)) return <Spoiler key={key}>{inline(t.slice(2, -2), ctx, key)}</Spoiler>;
    if (/^\*.+\*$/.test(t)) return <em key={key}>{inline(t.slice(1, -1), ctx, key)}</em>;
    return <Fragment key={key}>{t}</Fragment>;
  });
}

function Spoiler({ children }) {
  const [shown, setShown] = useState(false);
  return <span onClick={() => setShown(true)} className={`rounded px-0.5 ${shown ? "bg-white/10" : "bg-white/40 text-transparent cursor-pointer select-none"}`}>{children}</span>;
}

// Avatar Discord, posé sur les initiales (visibles tant que l'image charge, ou si elle échoue).
function Avatar({ user, size = "w-10 h-10" }) {
  const [broken, setBroken] = useState(false);
  const hue = [...(user.id || "0")].reduce((n, c) => n + c.charCodeAt(0), 0) % 360;
  return (
    <span className={`${size} relative shrink-0 rounded-full overflow-hidden flex items-center justify-center text-xs font-semibold text-white`} style={{ background: `hsl(${hue} 55% 45%)` }}>
      {(user.name || "?").slice(0, 2).toUpperCase()}
      {user.avatar_url && !broken && <img src={user.avatar_url} alt="" className="absolute inset-0 w-full h-full object-cover" onError={() => setBroken(true)} />}
    </span>
  );
}

// ── Messages ──

function Embed({ e, ctx }) {
  return (
    <div className="mt-1 max-w-lg rounded border-l-4 bg-[#2b2d31] p-3 text-sm grid gap-1.5" style={{ borderColor: e.color || "#1e1f22" }}>
      <div className="flex gap-3">
        <div className="min-w-0 flex-1 grid gap-1">
          {e.author.name && <div className="flex items-center gap-2 text-xs font-semibold">{e.author.icon_url && <img src={e.author.icon_url} alt="" className="w-5 h-5 rounded-full" />}{e.author.name}</div>}
          {e.title && (e.url ? <a href={e.url} target="_blank" rel="noopener noreferrer" className="font-semibold text-sky-400 hover:underline">{e.title}</a> : <div className="font-semibold">{e.title}</div>)}
          {e.description && <div className="whitespace-pre-wrap break-words text-white/85"><Markdown text={e.description} ctx={ctx} /></div>}
          {e.fields.length > 0 && (
            <div className="grid grid-cols-3 gap-2">
              {e.fields.map((f, i) => (
                <div key={i} className={f.inline ? "col-span-1" : "col-span-3"}>
                  <div className="text-xs font-semibold">{f.name}</div>
                  <div className="text-xs whitespace-pre-wrap text-white/80"><Markdown text={f.value} ctx={ctx} /></div>
                </div>
              ))}
            </div>
          )}
        </div>
        {e.thumbnail && <img src={e.thumbnail} alt="" className="w-16 h-16 rounded object-cover shrink-0" />}
      </div>
      {e.image && <img src={e.image} alt="" className="rounded max-h-72 object-contain" />}
      {e.footer.text && <div className="flex items-center gap-2 text-[11px] text-white/50">{e.footer.icon_url && <img src={e.footer.icon_url} alt="" className="w-4 h-4 rounded-full" />}{e.footer.text}</div>}
    </div>
  );
}

function Attachment({ a }) {
  if (a.content_type.startsWith("image/")) {
    return <a href={a.url} target="_blank" rel="noopener noreferrer"><img src={a.url} alt={a.name} className="mt-1 rounded max-h-72 max-w-sm object-contain" /></a>;
  }
  if (a.content_type.startsWith("video/")) return <video src={a.url} controls className="mt-1 rounded max-h-72 max-w-sm" />;
  return (
    <a href={a.url} target="_blank" rel="noopener noreferrer" className="mt-1 inline-flex items-center gap-2 rounded bg-[#2b2d31] border border-white/5 px-3 py-2 text-sm text-sky-400 hover:underline">
      <Paperclip className="w-4 h-4" />{a.name}<span className="text-white/40 text-xs">{Math.ceil(a.size / 1024)} Ko</span>
    </a>
  );
}

function Message({ m, grouped, ctx, onReply, onReact, onEdit, onDelete, editing, setEditing, onMenu, onAuthorMenu }) {
  const [draft, setDraft] = useState(m.content);
  const editRef = useRef(null);
  const [picker, setPicker] = useState(false);
  useEffect(() => { if (editing) setDraft(m.content); }, [editing, m.content]);
  const name = m.author.nick || ctx.memberName(m.author.id) || m.author.name;
  const color = ctx.memberColor(m.author.id);
  const system = ![0, 19, 20].includes(m.type);
  const saveEdit = () => { if (draft.trim() && draft !== m.content) onEdit(m, draft); setEditing(null); };
  return (
    <div className={`group relative flex gap-3 px-4 ${grouped ? "py-0.5" : "pt-3 pb-0.5"} hover:bg-white/[0.03] ${m.pinned ? "bg-amber-500/[0.04]" : ""}`} data-testid={`console-msg-${m.id}`}
      onContextMenu={(e) => { if (!editing) onMenu(e, m); }}>
      <div className="w-10 shrink-0">
        {!grouped && <button type="button" onClick={(e) => onAuthorMenu(e, m.author)} onContextMenu={(e) => { e.stopPropagation(); onAuthorMenu(e, m.author); }} className="rounded-full"><Avatar user={m.author} /></button>}
      </div>
      <div className="min-w-0 flex-1">
        {m.reply && (
          <div className="flex items-center gap-1.5 text-xs text-white/50 mb-0.5 truncate">
            <Reply className="w-3 h-3 -scale-x-100" /><Avatar user={m.reply.author} size="w-4 h-4 text-[7px]" />
            <span className="font-medium text-white/70">{m.reply.author.name}</span><span className="truncate">{m.reply.content || "message"}</span>
          </div>
        )}
        {!grouped && (
          <div className="flex items-baseline gap-2">
            <button type="button" onClick={(e) => onAuthorMenu(e, m.author)} onContextMenu={(e) => { e.stopPropagation(); onAuthorMenu(e, m.author); }}
              className="font-medium text-[15px] hover:underline" style={color ? { color } : undefined} data-testid={`console-author-${m.id}`}>{name}</button>
            {m.author.bot && <span className="text-[10px] font-semibold px-1 rounded bg-indigo-500 text-white leading-4">BOT</span>}
            <span className="text-[11px] text-white/40">{when(m.timestamp)}</span>
            {m.pinned && <Pin className="w-3 h-3 text-amber-300" title="Message épinglé" />}
          </div>
        )}
        {editing ? (
          <div className="mt-1">
            <div className="relative">
              <textarea ref={editRef} autoFocus value={draft} onChange={(e) => setDraft(e.target.value)} maxLength={2000} rows={Math.min(8, draft.split("\n").length + 1)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); saveEdit(); } if (e.key === "Escape") setEditing(null); }}
                className="w-full rounded bg-[#383a40] p-2 text-sm text-white resize-none focus:outline-none" data-testid="console-edit-input" />
              <DiscordFormatBar inputRef={editRef} value={draft} onChange={setDraft} maxLength={2000} />
            </div>
            <div className="text-[11px] text-white/50">Échap pour <button type="button" onClick={() => setEditing(null)} className="text-sky-400">annuler</button> · Entrée pour <button type="button" onClick={saveEdit} className="text-sky-400">enregistrer</button></div>
          </div>
        ) : (
          <div className={`text-[15px] leading-relaxed whitespace-pre-wrap break-words ${system ? "italic text-white/50" : "text-white/90"}`}>
            {system && !m.content ? "Message système" : <Markdown text={m.content} ctx={{ ...ctx, users: { ...ctx.users, ...m.mentions } }} />}
            {m.edited && <span className="text-[10px] text-white/40"> (modifié)</span>}
          </div>
        )}
        {m.attachments.map((a) => <Attachment key={a.id} a={a} />)}
        {m.embeds.filter((e) => e.type === "rich" || e.title || e.description).map((e, i) => <Embed key={i} e={e} ctx={ctx} />)}
        {m.stickers.map((s, i) => <div key={i} className="mt-1 text-xs text-white/50">[autocollant : {s}]</div>)}
        {m.components && <div className="mt-1 text-[11px] text-white/40">Boutons ou menus attachés à ce message</div>}
        {m.reactions.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1">
            {m.reactions.map((r) => (
              <button key={r.key} type="button" onClick={() => onReact(m, r.key, r.me)} title={r.me ? "Retirer la réaction du bot" : "Réagir avec le bot"}
                className={`flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-xs ${r.me ? "border-indigo-400 bg-indigo-500/20" : "border-transparent bg-[#2b2d31] hover:border-white/20"}`}>
                {r.url ? <img src={r.url} alt={r.name} className="w-4 h-4" /> : <span>{r.name}</span>}<span>{r.count}</span>
              </button>
            ))}
          </div>
        )}
      </div>
      {!editing && (
        <div className="absolute -top-3 right-4 hidden group-hover:flex items-center rounded-md border border-white/10 bg-[#313338] shadow-lg">
          <IconBtn title="Réagir" onClick={() => setPicker((v) => !v)}><SmilePlus className="w-4 h-4" /></IconBtn>
          <IconBtn title="Répondre" onClick={() => onReply(m)} testId={`console-reply-${m.id}`}><Reply className="w-4 h-4" /></IconBtn>
          {m.mine && <IconBtn title="Modifier" onClick={() => setEditing(m.id)} testId={`console-edit-${m.id}`}><Pencil className="w-4 h-4" /></IconBtn>}
          <IconBtn title="Supprimer" danger onClick={() => onDelete(m)} testId={`console-delete-${m.id}`}><Trash2 className="w-4 h-4" /></IconBtn>
          <IconBtn title="Plus" onClick={(e) => onMenu(e, m)} testId={`console-more-${m.id}`}><MoreHorizontal className="w-4 h-4" /></IconBtn>
        </div>
      )}
      {picker && (
        <div className="absolute top-6 right-4 z-10 flex gap-1 rounded-md border border-white/10 bg-[#313338] p-1.5 shadow-xl" onMouseLeave={() => setPicker(false)}>
          {QUICK_EMOJIS.map((e) => <button key={e} type="button" onClick={() => { setPicker(false); onReact(m, e, false); }} className="w-8 h-8 rounded hover:bg-white/10 text-lg">{e}</button>)}
        </div>
      )}
    </div>
  );
}

function IconBtn({ children, title, onClick, danger, testId }) {
  return (
    <button type="button" title={title} aria-label={title} onClick={onClick} data-testid={testId}
      className={`p-1.5 ${danger ? "text-red-400 hover:bg-red-500/20" : "text-white/70 hover:bg-white/10 hover:text-white"} first:rounded-l-md last:rounded-r-md`}>
      {children}
    </button>
  );
}

// ── Colonnes ──

function GuildRail({ guilds, current, onPick, dmsActive, onMenu, onAdd }) {
  return (
    <nav className="w-[72px] shrink-0 bg-[#1e1f22] flex flex-col items-center gap-2 py-3 overflow-y-auto" data-testid="console-guilds">
      <button type="button" title="Messages privés" onClick={() => onPick("@dms")}
        className={`w-12 h-12 flex items-center justify-center transition-all ${dmsActive ? "rounded-2xl bg-indigo-500" : "rounded-3xl bg-[#313338] hover:rounded-2xl hover:bg-indigo-500"}`}>
        <MessageCircle className="w-5 h-5" />
      </button>
      <div className="w-8 h-0.5 bg-white/10 rounded" />
      {guilds.map((g) => (
        <button key={g.id} type="button" title={g.name} onClick={() => onPick(g.id)} onContextMenu={(e) => onMenu(e, g)} data-testid={`console-guild-${g.id}`} className="relative">
          {current === g.id && <span className="absolute -left-3 top-2 w-1 h-8 rounded-r bg-white" />}
          <GuildIcon g={g} active={current === g.id} />
        </button>
      ))}
      {onAdd && (
        <button type="button" title="Ajouter le bot à un serveur" aria-label="Ajouter le bot à un serveur" onClick={onAdd} data-testid="console-addbot-open-rail"
          className="w-12 h-12 shrink-0 flex items-center justify-center rounded-3xl bg-[#313338] text-[#23a559] transition-all hover:rounded-2xl hover:bg-[#23a559] hover:text-white">
          <Plus className="w-6 h-6" />
        </button>
      )}
    </nav>
  );
}

// Icône du serveur, ou ses initiales si elle manque ou ne se charge pas.
function GuildIcon({ g, active }) {
  const [broken, setBroken] = useState(false);
  useEffect(() => setBroken(false), [g.icon_url]);
  const shape = active ? "rounded-2xl" : "rounded-3xl hover:rounded-2xl";
  if (g.icon_url && !broken) return <img src={g.icon_url} alt="" onError={() => setBroken(true)} className={`w-12 h-12 object-cover transition-all ${shape}`} />;
  return <span className={`w-12 h-12 flex items-center justify-center bg-[#313338] text-sm font-semibold transition-all ${active ? "rounded-2xl bg-indigo-500" : shape}`}>{g.name.split(/\s+/).map((w) => w[0]).join("").slice(0, 3)}</span>;
}

const CHANNEL_ICONS = { text: Hash, announcement: Megaphone, voice: Volume2, stage: Volume2, forum: MessagesSquare, media: MessagesSquare };

function ChannelList({ title, channels, current, onPick, footer, onServerMenu, onChannelMenu, onInvite }) {
  return (
    <aside className="w-60 shrink-0 bg-[#2b2d31] flex flex-col min-h-0">
      <div className="h-12 flex items-stretch border-b border-black/30">
        <button type="button" onClick={(e) => { const r = e.currentTarget.getBoundingClientRect(); onServerMenu({ preventDefault() {}, clientX: r.left + 8, clientY: r.bottom + 4 }); }}
          onContextMenu={onServerMenu} data-testid="console-server-header"
          className="min-w-0 flex-1 pl-4 pr-2 flex items-center gap-2 font-semibold hover:bg-white/[0.04] text-left">
          <span className="truncate flex-1">{title}</span><ChevronDown className="w-4 h-4 shrink-0" />
        </button>
        {onInvite && (
          <button type="button" onClick={onInvite} title="Inviter des gens" aria-label="Inviter des gens" data-testid="console-invite-open"
            className="px-3 text-white/60 hover:bg-white/[0.04] hover:text-white"><UserPlus className="w-[18px] h-[18px]" /></button>
        )}
      </div>
      <div className="flex-1 overflow-y-auto py-2 px-2 space-y-0.5" data-testid="console-channels" onContextMenu={(e) => { if (e.target === e.currentTarget) onChannelMenu(e, null); }}>
        {channels.map((c) => {
          if (c.kind === "category") return <div key={c.id} onContextMenu={(e) => onChannelMenu(e, c)} data-testid={`console-category-${c.id}`} className="pt-4 pb-1 px-1 text-[11px] font-semibold uppercase tracking-wide text-white/50 truncate cursor-default">{c.name}</div>;
          const Icon = CHANNEL_ICONS[c.kind] || Hash;
          return (
            <button key={c.id} type="button" onClick={() => { if (c.writable) onPick(c); }} onContextMenu={(e) => onChannelMenu(e, c)} data-testid={`console-channel-${c.id}`}
              title={c.writable ? c.topic || c.name : "Salon vocal ou forum : pas de messages ici"}
              className={`w-full flex items-center gap-1.5 rounded px-2 py-1.5 text-sm text-left ${current === c.id ? "bg-white/10 text-white" : "text-white/55 hover:bg-white/5 hover:text-white/85"} ${c.writable ? "" : "opacity-50 cursor-default"}`}>
              <Icon className="w-4 h-4 shrink-0" /><span className="truncate">{c.name}</span>{c.nsfw && <Lock className="w-3 h-3 ml-auto shrink-0" />}
            </button>
          );
        })}
      </div>
      {footer}
    </aside>
  );
}

function BotFooter({ bot, onLog, onProfile }) {
  return (
    <div className="h-14 shrink-0 bg-[#232428] px-2 flex items-center gap-2">
      <button type="button" onClick={onProfile} title="Modifier le profil du bot" className="flex min-w-0 flex-1 items-center gap-2 rounded px-1 py-1 text-left hover:bg-white/[0.06]" data-testid="console-profile-open">
        <Avatar user={bot.user} size="w-8 h-8" />
        <div className="min-w-0 flex-1 leading-tight">
          <div className="text-sm font-medium truncate">{bot.user.name}</div>
          <div className="text-[11px] text-white/50 truncate">Connecté en tant que bot</div>
        </div>
      </button>
      <button type="button" title="Profil du bot" onClick={onProfile} className="p-1.5 rounded text-white/60 hover:bg-white/10 hover:text-white"><Settings className="w-4 h-4" /></button>
      <button type="button" title="Journal de la console" onClick={onLog} className="p-1.5 rounded text-white/60 hover:bg-white/10 hover:text-white" data-testid="console-log-open"><ScrollText className="w-4 h-4" /></button>
    </div>
  );
}

function DMList({ dms, current, onPick, onOpen, onClose }) {
  const [userId, setUserId] = useState("");
  return (
    <div className="flex-1 overflow-y-auto py-2 px-2 space-y-1">
      <form onSubmit={(e) => { e.preventDefault(); if (userId.trim()) { onOpen(userId.trim()); setUserId(""); } }} className="flex gap-1 mb-2">
        <input value={userId} onChange={(e) => setUserId(e.target.value.replace(/\D/g, ""))} placeholder="ID Discord du membre" maxLength={25}
          className="flex-1 min-w-0 rounded bg-[#1e1f22] px-2 py-1.5 text-xs focus:outline-none" data-testid="console-dm-input" />
        <button type="submit" title="Ouvrir un MP" className="rounded bg-indigo-500 px-2 hover:bg-indigo-400" data-testid="console-dm-open"><Plus className="w-4 h-4" /></button>
      </form>
      <p className="px-1 text-[11px] text-white/40">Clic droit sur un membre dans Discord › « Copier l'identifiant » (mode développeur).</p>
      {dms.map((d) => (
        <div key={d.channel_id} className={`group flex items-center gap-2 rounded px-2 py-1.5 ${current === d.channel_id ? "bg-white/10" : "hover:bg-white/5"}`}>
          <button type="button" onClick={() => onPick(d)} className="flex items-center gap-2 min-w-0 flex-1 text-left" data-testid={`console-dm-${d.user.id}`}>
            <Avatar user={d.user} size="w-8 h-8" /><span className="text-sm truncate">{d.user.name}</span>
          </button>
          <button type="button" title="Retirer de la liste" onClick={() => onClose(d)} className="hidden group-hover:block text-white/50 hover:text-white"><X className="w-4 h-4" /></button>
        </div>
      ))}
    </div>
  );
}

function Members({ data, roles, onMenu }) {
  if (!data) return <aside className="w-60 shrink-0 bg-[#2b2d31] p-4 text-sm text-white/50 hidden lg:block">Chargement…</aside>;
  return (
    <aside className="w-60 shrink-0 bg-[#2b2d31] overflow-y-auto p-3 hidden lg:block" data-testid="console-members">
      <div className="px-1 pb-2 text-[11px] font-semibold uppercase tracking-wide text-white/50">Membres — {data.members.length}</div>
      {data.error && <p className="px-1 text-xs text-amber-200">{data.error}</p>}
      {data.members.map((m) => {
        const role = m.roles.map((r) => roles[r]).filter((r) => r && r.color).sort((a, b) => b.position - a.position)[0];
        return (
          <button type="button" key={m.id} onClick={(e) => onMenu(e, m)} onContextMenu={(e) => onMenu(e, m)} data-testid={`console-member-${m.id}`}
            className="w-full flex items-center gap-2 rounded px-1 py-1 hover:bg-white/5 text-left" title={`${m.username} · ${m.id}`}>
            <Avatar user={m} size="w-8 h-8" />
            <span className="text-sm truncate" style={role ? { color: role.color } : undefined}>{m.nick || m.name}</span>
            {m.bot && <span className="text-[10px] font-semibold px-1 rounded bg-indigo-500 leading-4">BOT</span>}
            {m.timeout_until && new Date(m.timeout_until) > new Date() && <Clock className="w-3.5 h-3.5 shrink-0 text-amber-300" title="Exclu temporairement" />}
          </button>
        );
      })}
    </aside>
  );
}

function LogModal({ botId, onClose }) {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get(`/${botId}/console/log`).then((r) => setRows(r.log)).catch((err) => { toast.error(errorText(err, "Journal indisponible")); onClose(); }); }, [botId, onClose]);
  const label = {
    send: "Envoi", edit: "Modification", delete: "Suppression", guild: "Serveur", leave: "Départ du serveur", profile: "Profil du bot", nick: "Pseudo du bot",
    channel_create: "Salon créé", channel_edit: "Salon modifié", channel_delete: "Salon supprimé", role_create: "Rôle créé", role_edit: "Rôle modifié",
    role_delete: "Rôle supprimé", invite: "Invitation", member: "Membre", kick: "Expulsion", ban: "Bannissement", unban: "Débannissement", pin: "Épingle",
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="w-full max-w-2xl max-h-[80vh] overflow-y-auto rounded-xl bg-[#313338] border border-white/10 p-5" onClick={(e) => e.stopPropagation()} data-testid="console-log">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-semibold flex items-center gap-2"><ScrollText className="w-4 h-4" />Journal de la console</h2>
          <button type="button" onClick={onClose} className="text-white/60 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        {!rows ? <p className="text-sm text-white/50">Chargement…</p> : rows.length === 0 ? <p className="text-sm text-white/50">Rien pour l'instant.</p> : (
          <ul className="space-y-2">
            {rows.map((r) => (
              <li key={r.id} className="text-sm rounded bg-black/20 px-3 py-2">
                <div className="text-xs text-white/50">{when(r.at)} · {r.user_name} · {label[r.action] || r.action} · {r.channel_name === "MP" ? "MP" : r.channel_id ? `#${r.channel_name}` : r.channel_name}</div>
                <div className="truncate">{r.summary}</div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

// ── Zone de saisie ──

function Composer({ channel, replyTo, setReplyTo, onSend, sending, botId, insert }) {
  const [text, setText] = useState("");
  const [file, setFile] = useState(null);
  const [everyone, setEveryone] = useState(false);
  const fileRef = useRef(null);
  const inputRef = useRef(null);
  const lastTyping = useRef(0);
  useEffect(() => { setText(""); setFile(null); setEveryone(false); }, [channel.id]);
  // « Mentionner » depuis le clic droit sur un membre.
  useEffect(() => { if (insert?.text) setText((t) => `${t && !t.endsWith(" ") ? `${t} ` : t}${insert.text} `); }, [insert]);
  const pick = (f) => {
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) { toast.error("Fichier trop lourd (8 Mo maximum)"); return; }
    const r = new FileReader();
    r.onload = () => setFile({ name: f.name, data: r.result, type: f.type });
    r.readAsDataURL(f);
  };
  const typing = () => {
    if (Date.now() - lastTyping.current < 8000) return;
    lastTyping.current = Date.now();
    api.post(`/${botId}/console/channels/${channel.id}/typing`).catch(() => {});
  };
  const send = async () => {
    if (sending || (!text.trim() && !file)) return;
    const ok = await onSend({ content: text, file: file ? { name: file.name, data: file.data } : null, reply_to: replyTo?.id || "", mention_everyone: everyone });
    if (ok) { setText(""); setFile(null); setEveryone(false); setReplyTo(null); lastTyping.current = 0; }
  };
  const mentionsAll = /@(everyone|here)\b/.test(text);
  return (
    <div className="px-4 pb-5 shrink-0">
      {replyTo && (
        <div className="flex items-center gap-2 rounded-t-lg bg-[#2b2d31] px-3 py-1.5 text-xs text-white/60">
          Réponse à <span className="font-medium text-white">{replyTo.author.nick || replyTo.author.name}</span>
          <button type="button" onClick={() => setReplyTo(null)} className="ml-auto hover:text-white"><X className="w-4 h-4" /></button>
        </div>
      )}
      {file && (
        <div className="flex items-center gap-3 bg-[#2b2d31] px-3 py-2 border-b border-black/20">
          {file.type.startsWith("image/") ? <img src={file.data} alt="" className="h-16 rounded" /> : <Paperclip className="w-5 h-5" />}
          <span className="text-xs truncate">{file.name}</span>
          <button type="button" onClick={() => setFile(null)} className="ml-auto text-white/60 hover:text-white"><X className="w-4 h-4" /></button>
        </div>
      )}
      <div className={`flex items-end gap-2 bg-[#383a40] px-3 py-2 ${replyTo || file ? "rounded-b-lg" : "rounded-lg"}`}>
        <input type="file" ref={fileRef} className="hidden" accept="image/png,image/jpeg,image/gif,image/webp,video/mp4,text/plain,application/pdf"
          onChange={(e) => { pick(e.target.files?.[0]); e.target.value = ""; }} data-testid="console-file" />
        <button type="button" title="Joindre un fichier" onClick={() => fileRef.current?.click()} className="p-1 mb-0.5 text-white/60 hover:text-white"><Paperclip className="w-5 h-5" /></button>
        <div className="relative flex-1 flex">
          <textarea ref={inputRef} value={text} rows={Math.min(8, text.split("\n").length)} maxLength={2000} disabled={sending}
            onChange={(e) => { setText(e.target.value); typing(); }}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder={`Envoyer un message ${channel.kind === "dm" ? "en MP" : `dans #${channel.name}`} avec le bot`}
            className="flex-1 bg-transparent resize-none py-1 text-[15px] text-white placeholder:text-white/40 focus:outline-none max-h-48" data-testid="console-input" />
          <DiscordFormatBar inputRef={inputRef} value={text} onChange={setText} disabled={sending} maxLength={2000} />
        </div>
        <button type="button" title="Envoyer" onClick={send} disabled={sending || (!text.trim() && !file)} className="p-1 mb-0.5 text-white/60 hover:text-white disabled:opacity-30" data-testid="console-send">
          {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
        </button>
      </div>
      <div className="flex items-center gap-3 mt-1 text-[11px] text-white/40">
        <span>Entrée pour envoyer · Maj+Entrée pour aller à la ligne · {text.length}/2000</span>
        {mentionsAll && (
          <label className="flex items-center gap-1 text-amber-200 cursor-pointer">
            <input type="checkbox" checked={everyone} onChange={(e) => setEveryone(e.target.checked)} data-testid="console-everyone" />
            <AtSign className="w-3 h-3" />Notifier tout le monde (@everyone / @here)
          </label>
        )}
      </div>
    </div>
  );
}

// ── Page ──

export default function BotConsolePage() {
  const { botId } = useParams();
  const [home, setHome] = useState(null);
  const [error, setError] = useState(null);
  const [guildId, setGuildId] = useState(null);
  const [guild, setGuild] = useState(null);
  const [channel, setChannel] = useState(null);
  const [messages, setMessages] = useState([]);
  const [info, setInfo] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [editing, setEditing] = useState(null);
  const [sending, setSending] = useState(false);
  const [showMembers, setShowMembers] = useState(true);
  const [members, setMembers] = useState(null);
  const [showLog, setShowLog] = useState(false);
  const [mobilePane, setMobilePane] = useState("nav");
  const [menu, setMenu] = useState(null);       // clic droit : { x, y, kind, target }
  const [dialog, setDialog] = useState(null);   // fenêtre ouverte : { type, ... }
  const [insert, setInsert] = useState(null);   // texte à ajouter dans la zone de saisie
  const scroller = useRef(null);
  const atBottom = useRef(true);
  const channelRef = useRef(null);

  const loadHome = useCallback(() => api.get(`/${botId}/console`).then((h) => { setHome(h); return h; }), [botId]);
  useEffect(() => {
    loadHome().then((h) => setGuildId((g) => g || h.guilds[0]?.id || "@dms")).catch((err) => setError(errorText(err, "Console indisponible")));
  }, [loadHome]);

  // Serveur choisi : salons et rôles (relus toutes les 30 s), membres.
  useEffect(() => {
    if (!guildId || guildId === "@dms") { setGuild(null); setMembers(null); return undefined; }
    let stop = false;
    const load = () => api.get(`/${botId}/console/guilds/${guildId}`).then((g) => { if (!stop) setGuild(g); }).catch((err) => { if (!stop) toast.error(errorText(err, "Serveur indisponible")); });
    setGuild(null); setMembers(null);
    load();
    api.get(`/${botId}/console/guilds/${guildId}/members`).then((m) => { if (!stop) setMembers(m); }).catch(() => { if (!stop) setMembers({ members: [], error: "Membres indisponibles" }); });
    const t = setInterval(load, 30000);
    return () => { stop = true; clearInterval(t); };
  }, [botId, guildId]);

  // Premier salon où le bot peut écrire, quand on change de serveur.
  useEffect(() => {
    if (!guild || (channel && channel.guildId === guildId)) return;
    const first = guild.channels.find((c) => c.writable);
    if (first) setChannel({ ...first, guildId });
  }, [guild, guildId, channel]);

  // Messages du salon : relus toutes les 4 s (nouveaux, modifiés, supprimés, réactions).
  const fetchLatest = useCallback(async (ch, initial) => {
    try {
      const r = await api.get(`/${botId}/console/channels/${ch.id}/messages`);
      if (channelRef.current !== ch.id) return;
      setInfo(r);
      if (initial) setHasMore(r.has_more);
      setMessages((prev) => {
        if (initial || !r.messages.length) return r.messages;
        const oldest = r.messages[0].id;
        return [...prev.filter((m) => idCmp(m.id, oldest) < 0), ...r.messages];
      });
    } catch (err) {
      if (initial) { toast.error(errorText(err, "Salon illisible")); setMessages([]); setInfo(null); }
    }
  }, [botId]);
  useEffect(() => {
    if (!channel) return undefined;
    channelRef.current = channel.id;
    atBottom.current = true;
    setMessages([]); setInfo(null); setReplyTo(null); setEditing(null);
    fetchLatest(channel, true);
    const t = setInterval(() => { if (!document.hidden) fetchLatest(channel, false); }, POLL_MS);
    return () => clearInterval(t);
  }, [channel, fetchLatest]);

  useEffect(() => {
    const el = scroller.current;
    if (el && atBottom.current) el.scrollTop = el.scrollHeight;
  }, [messages]);

  const loadOlder = async () => {
    if (!messages.length || loadingMore) return;
    setLoadingMore(true);
    const el = scroller.current;
    const before = el ? el.scrollHeight : 0;
    try {
      const r = await api.get(`/${botId}/console/channels/${channel.id}/messages`, { before: messages[0].id });
      setHasMore(r.has_more);
      atBottom.current = false;
      setMessages((prev) => [...r.messages.filter((m) => !prev.some((p) => p.id === m.id)), ...prev].sort(byId));
      requestAnimationFrame(() => { if (el) el.scrollTop = el.scrollHeight - before; });
    } catch (err) { toast.error(errorText(err, "Impossible de charger la suite")); }
    setLoadingMore(false);
  };

  const ctx = useMemo(() => {
    const byMember = Object.fromEntries((members?.members || []).map((m) => [m.id, m]));
    const roles = guild?.roles || {};
    return {
      users: {},
      roles,
      channelName: (id) => guild?.channels.find((c) => c.id === id)?.name,
      memberName: (id) => byMember[id]?.nick || byMember[id]?.name,
      memberColor: (id) => (byMember[id]?.roles || []).map((r) => roles[r]).filter((r) => r && r.color).sort((a, b) => b.position - a.position)[0]?.color,
    };
  }, [guild, members]);

  const send = async (body) => {
    setSending(true);
    try {
      const r = await api.post(`/${botId}/console/channels/${channel.id}/messages`, body);
      atBottom.current = true;
      if (r.message) setMessages((prev) => (prev.some((m) => m.id === r.message.id) ? prev : [...prev, r.message]));
      setTimeout(() => fetchLatest(channel, false), 600);
      return true;
    } catch (err) { toast.error(errorText(err, "Envoi impossible")); return false; } finally { setSending(false); }
  };
  const edit = async (m, content) => {
    try {
      const r = await api.patch(`/${botId}/console/channels/${channel.id}/messages/${m.id}`, { content });
      if (r.message) setMessages((prev) => prev.map((x) => (x.id === m.id ? r.message : x)));
    } catch (err) { toast.error(errorText(err, "Modification impossible")); }
  };
  const remove = async (m) => {
    if (!window.confirm(m.mine ? "Supprimer ce message du bot ?" : `Supprimer le message de ${m.author.name} ? (le bot doit avoir « Gérer les messages »)`)) return;
    try {
      await api.del(`/${botId}/console/channels/${channel.id}/messages/${m.id}`);
      setMessages((prev) => prev.filter((x) => x.id !== m.id));
    } catch (err) { toast.error(errorText(err, "Suppression impossible")); }
  };
  const react = async (m, emoji, removeIt) => {
    try {
      await api.post(`/${botId}/console/channels/${channel.id}/messages/${m.id}/reactions`, { emoji, remove: removeIt });
      fetchLatest(channel, false);
    } catch (err) { toast.error(errorText(err, "Réaction impossible")); }
  };
  const openDM = async (userId) => {
    try {
      const r = await api.post(`/${botId}/console/dms`, { user_id: userId });
      await loadHome();
      setChannel({ id: r.dm.channel_id, name: r.dm.user.name, kind: "dm", writable: true, guildId: "@dms", user: r.dm.user });
      setMobilePane("chat");
    } catch (err) { toast.error(errorText(err, "MP impossible")); }
  };
  const closeDM = async (d) => {
    try { await api.del(`/${botId}/console/dms/${d.channel_id}`); await loadHome(); if (channel?.id === d.channel_id) setChannel(null); } catch (err) { toast.error(errorText(err, "Erreur")); }
  };

  const pickGuild = (id) => { setGuildId(id); setChannel(null); setMessages([]); };
  const closeLog = useCallback(() => setShowLog(false), []);

  // ── Clic droit, comme dans Discord ──
  const closeMenu = useCallback(() => setMenu(null), []);
  const closeDialog = useCallback(() => setDialog(null), []);
  const openMenu = (kind) => (e, target) => {
    e.preventDefault();
    e.stopPropagation?.();
    setMenu({ x: e.clientX, y: e.clientY, kind, target });
  };
  const refreshGuild = useCallback(async (withMembers = false) => {
    if (!guildId || guildId === "@dms") return;
    try {
      const g = await api.get(`/${botId}/console/guilds/${guildId}`);
      setGuild(g);
      if (withMembers) setMembers(await api.get(`/${botId}/console/guilds/${guildId}/members`));
    } catch (err) { toast.error(errorText(err, "Serveur indisponible")); }
  }, [botId, guildId]);
  const copy = (text, what = "Copié") => {
    const done = () => toast.success(what);
    if (navigator.clipboard?.writeText) navigator.clipboard.writeText(text).then(done, () => toast.error("Copie impossible"));
    else toast.error("Copie impossible");
  };
  const act = async (fn, ok, fallback) => {
    try { await fn(); if (ok) toast.success(ok); return true; } catch (err) { toast.error(errorText(err, fallback)); return false; }
  };
  const patchMember = (id, patch) => setMembers((prev) => prev && ({ ...prev, members: prev.members.map((x) => (x.id === id ? { ...x, ...patch(x) } : x)) }));
  const pin = (m) => act(async () => {
    await api.post(`/${botId}/console/channels/${channel.id}/messages/${m.id}/pin`, { pinned: !m.pinned });
    setMessages((prev) => prev.map((x) => (x.id === m.id ? { ...x, pinned: !m.pinned } : x)));
  }, m.pinned ? "Message désépinglé" : "Message épinglé", "Impossible d'épingler");

  const menuItems = () => {
    if (!menu) return [];
    const g = guild;
    const me = g?.me || { perms: {}, top_position: 0 };
    const perms = me.perms || {};
    const gid = guildId;
    const no = (perm, text) => (perms[perm] ? {} : { disabled: true, hint: `Le bot n'a pas la permission « ${text} »` });

    if (menu.kind === "guild") {
      const target = menu.target;
      const here = target.id === gid;
      return [
        { header: target.name },
        ...(here ? [{ label: "Inviter des gens", icon: UserPlus, onClick: () => setDialog({ type: "invite" }), testId: "menu-invite", accent: true }] : []),
        here ? { label: "Paramètres du serveur", icon: Settings, onClick: () => setDialog({ type: "server", tab: "overview" }), testId: "menu-server-settings" }
          : { label: "Ouvrir le serveur", icon: Settings, onClick: () => pickGuild(target.id) },
        ...(here ? [
          { label: "Rôles", icon: Shield, onClick: () => setDialog({ type: "server", tab: "roles" }), testId: "menu-server-roles" },
          { label: "Bannissements", icon: Gavel, onClick: () => setDialog({ type: "server", tab: "bans" }) },
          "sep",
          { label: "Créer un salon", icon: Plus, onClick: () => setDialog({ type: "channel", kind: "text" }), ...no("manage_channels", "Gérer les salons"), testId: "menu-create-channel" },
          { label: "Créer une catégorie", icon: FolderPlus, onClick: () => setDialog({ type: "channel", kind: "category" }), ...no("manage_channels", "Gérer les salons") },
          "sep",
          { label: "Modifier le profil du bot", icon: UserCog, onClick: () => setDialog({ type: "profile" }) },
        ] : []),
        "sep",
        { label: "Copier l'identifiant du serveur", icon: Copy, onClick: () => copy(target.id, "Identifiant copié") },
        { label: "Quitter le serveur", icon: LogOut, danger: true, testId: "menu-leave",
          onClick: () => setDialog({ type: "confirm", title: `Quitter « ${target.name} »`, danger: true, confirm: "Quitter le serveur",
            text: "Le bot quittera ce serveur. Pour le faire revenir, il faudra le réinviter avec son lien d'invitation.",
            run: () => act(async () => {
              await api.post(`/${botId}/console/guilds/${target.id}/leave`);
              const h = await loadHome();
              pickGuild(h.guilds[0]?.id || "@dms");
            }, "Le bot a quitté le serveur", "Impossible de quitter le serveur") }) },
      ];
    }

    if (menu.kind === "channel") {
      const c = menu.target;
      if (!c) {
        return [
          { label: "Créer un salon", icon: Plus, onClick: () => setDialog({ type: "channel", kind: "text" }), ...no("manage_channels", "Gérer les salons") },
          { label: "Créer une catégorie", icon: FolderPlus, onClick: () => setDialog({ type: "channel", kind: "category" }), ...no("manage_channels", "Gérer les salons") },
        ];
      }
      const cat = c.kind === "category";
      return [
        { header: cat ? c.name : `#${c.name}` },
        ...(c.writable && !cat ? [{ label: "Ouvrir le salon", icon: Hash, onClick: () => { setChannel({ ...c, guildId: gid }); setMobilePane("chat"); } }, "sep"] : []),
        ...(!cat ? [{ label: "Inviter des gens", icon: UserPlus, onClick: () => setDialog({ type: "invite", channelId: c.id }), testId: "menu-invite-channel" }] : []),
        { label: cat ? "Modifier la catégorie" : "Modifier le salon", icon: Pencil, onClick: () => setDialog({ type: "channel", channel: c }), ...no("manage_channels", "Gérer les salons"), testId: "menu-edit-channel" },
        { label: cat ? "Créer un salon dans la catégorie" : "Créer un salon", icon: Plus, onClick: () => setDialog({ type: "channel", kind: "text", parentId: cat ? c.id : c.parent_id }), ...no("manage_channels", "Gérer les salons") },
        "sep",
        ...(!cat ? [{ label: "Copier le lien", icon: Link2, onClick: () => copy(`https://discord.com/channels/${gid}/${c.id}`, "Lien copié") }] : []),
        { label: "Copier l'identifiant", icon: Copy, onClick: () => copy(c.id, "Identifiant copié") },
        { label: cat ? "Supprimer la catégorie" : "Supprimer le salon", icon: Trash2, danger: true, ...no("manage_channels", "Gérer les salons"), testId: "menu-delete-channel",
          onClick: () => setDialog({ type: "confirm", title: cat ? "Supprimer la catégorie" : "Supprimer le salon", danger: true, confirm: "Supprimer",
            text: cat ? `Supprimer la catégorie « ${c.name} » ? Ses salons resteront, sans catégorie.` : `Supprimer #${c.name} ? Tous ses messages seront perdus, c'est définitif.`,
            run: () => act(async () => {
              await api.del(`/${botId}/console/channels/${c.id}`);
              if (channel?.id === c.id) setChannel(null);
              await refreshGuild();
            }, cat ? "Catégorie supprimée" : "Salon supprimé", "Suppression impossible") }) },
      ];
    }

    if (menu.kind === "member") {
      const known = (members?.members || []).find((x) => x.id === menu.target.id);
      const m = known || { ...menu.target, nick: menu.target.nick || "", roles: null };
      const isBot = m.id === home?.bot.user.id;
      const ownerId = g?.info?.owner_id;
      const roleList = Object.values(g?.roles || {}).filter((r) => r.id !== gid).sort((a, b) => b.position - a.position);
      const memberTop = Math.max(0, ...(m.roles || []).map((r) => g?.roles?.[r]?.position || 0));
      // Discord : le bot n'agit que sur les membres dont le rôle le plus haut est sous le sien.
      const above = m.id === ownerId || memberTop >= (me.top_position || 0);
      const blocked = (perm, text) => (!perms[perm] ? { disabled: true, hint: `Le bot n'a pas la permission « ${text} »` }
        : above ? { disabled: true, hint: m.id === ownerId ? "Impossible sur le propriétaire du serveur" : "Ce membre a un rôle au-dessus de celui du bot" } : {});
      const timedOut = m.timeout_until && new Date(m.timeout_until) > new Date();
      const name = m.nick || m.name;
      const isDM = guildId === "@dms";
      if (isDM || !g) {
        return [
          { header: name },
          ...(!isBot ? [{ label: "Envoyer un message", icon: MessageCircle, onClick: () => openDM(m.id) }] : []),
          { label: "Copier l'identifiant", icon: Copy, onClick: () => copy(m.id, "Identifiant copié") },
        ];
      }
      const roleItems = !m.roles ? [{ label: "Membre pas encore chargé", disabled: true }] : roleList.map((r) => {
        const has = m.roles.includes(r.id);
        const locked = r.managed || r.position >= (me.top_position || 0) || !perms.manage_roles;
        return {
          label: r.name, color: r.color, checked: has, keep: true, disabled: locked,
          hint: r.managed ? "Rôle géré par une intégration" : !perms.manage_roles ? "Le bot n'a pas « Gérer les rôles »" : "Rôle au-dessus de celui du bot",
          testId: `menu-role-${r.id}`,
          onClick: () => act(async () => {
            await api.post(`/${botId}/console/guilds/${gid}/members/${m.id}/roles/${r.id}`, { add: !has });
            patchMember(m.id, (x) => ({ roles: has ? x.roles.filter((id) => id !== r.id) : [...x.roles, r.id] }));
          }, null, "Impossible de changer le rôle"),
        };
      });
      const timeouts = [[1, "60 secondes"], [5, "5 minutes"], [10, "10 minutes"], [60, "1 heure"], [1440, "1 jour"], [10080, "1 semaine"]].map(([min, label]) => ({
        label, testId: `menu-timeout-${min}`,
        onClick: () => act(async () => {
          await api.patch(`/${botId}/console/guilds/${gid}/members/${m.id}`, { timeout_minutes: min });
          patchMember(m.id, () => ({ timeout_until: new Date(Date.now() + min * 60000).toISOString() }));
        }, `${name} est exclu pour ${label}`, "Exclusion impossible"),
      }));
      return [
        { header: name },
        ...(!isBot ? [
          { label: "Envoyer un message", icon: MessageCircle, onClick: () => openDM(m.id), testId: "menu-dm" },
          { label: "Mentionner", icon: AtSign, onClick: () => setInsert({ text: `<@${m.id}>`, n: Date.now() }), disabled: !channel || channel.kind === "dm" },
          "sep",
          { label: "Changer le pseudo", icon: Tag, onClick: () => setDialog({ type: "nick", member: m }), ...blocked("manage_nicknames", "Gérer les pseudos"), testId: "menu-nick" },
          { label: "Rôles", sub: roleItems, testId: "menu-roles" },
          "sep",
          timedOut
            ? { label: "Retirer l'exclusion", icon: Clock, ...blocked("moderate", "Exclure temporairement des membres"),
              onClick: () => act(async () => { await api.patch(`/${botId}/console/guilds/${gid}/members/${m.id}`, { timeout_minutes: 0 }); patchMember(m.id, () => ({ timeout_until: "" })); }, "Exclusion retirée", "Impossible") }
            : { label: `Exclure temporairement ${name}`, sub: timeouts, ...blocked("moderate", "Exclure temporairement des membres"), testId: "menu-timeout" },
          { label: `Expulser ${name}`, icon: UserMinus, danger: true, onClick: () => setDialog({ type: "sanction", kind: "kick", member: m }), ...blocked("kick", "Expulser des membres"), testId: "menu-kick" },
          { label: `Bannir ${name}`, icon: Ban, danger: true, onClick: () => setDialog({ type: "sanction", kind: "ban", member: m }), ...blocked("ban", "Bannir des membres"), testId: "menu-ban" },
        ] : [
          { label: "Modifier le profil du bot", icon: UserCog, onClick: () => setDialog({ type: "profile" }) },
          { label: "Rôles", sub: roleItems },
        ]),
        "sep",
        { label: "Copier l'identifiant", icon: Copy, onClick: () => copy(m.id, "Identifiant copié") },
      ];
    }

    if (menu.kind === "message") {
      const m = menu.target;
      const inDM = channel?.kind === "dm";
      const authorKnown = !inDM && guild;
      return [
        { label: "Réagir", sub: QUICK_EMOJIS.map((e) => ({ label: e, onClick: () => react(m, e, false) })) },
        { label: "Répondre", icon: Reply, onClick: () => setReplyTo(m) },
        ...(m.mine ? [{ label: "Modifier le message", icon: Pencil, onClick: () => setEditing(m.id) }] : []),
        ...(!inDM ? [{ label: m.pinned ? "Désépingler le message" : "Épingler le message", icon: Pin, onClick: () => pin(m), testId: "menu-pin" }] : []),
        "sep",
        ...(m.content ? [{ label: "Copier le texte", icon: Copy, onClick: () => copy(m.content, "Texte copié") }] : []),
        { label: "Copier le lien du message", icon: Link2, onClick: () => copy(`https://discord.com/channels/${inDM ? "@me" : guildId}/${channel.id}/${m.id}`, "Lien copié") },
        { label: "Copier l'identifiant du message", icon: Copy, onClick: () => copy(m.id, "Identifiant copié") },
        ...(authorKnown && !m.mine ? ["sep", { label: `Membre : ${m.author.nick || m.author.name}`, icon: UserCog, keep: true,
          onClick: () => setMenu((cur) => cur && { ...cur, kind: "member", target: m.author }) }] : []),
        "sep",
        { label: "Supprimer le message", icon: Trash2, danger: true, onClick: () => remove(m), testId: "menu-delete-message" },
      ];
    }
    return [];
  };

  if (error) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] text-white"><Navbar />
        <main className="max-w-xl mx-auto px-4 pt-28">
          <div className="rounded-lg border border-white/10 bg-[#12121a] p-5 text-sm">{error}. <Link to={`/bot-dashboard/${botId}`} className="underline">Retour au bot</Link></div>
        </main>
      </div>
    );
  }
  if (!home) return <div className="min-h-screen bg-[#0a0a0f] text-white"><Navbar /><main className="pt-28 text-center text-sm text-white/50">Connexion au bot…</main></div>;

  const currentGuild = home.guilds.find((g) => g.id === guildId);
  const isDMs = guildId === "@dms";
  const ChatIcon = channel?.kind === "dm" ? AtSign : CHANNEL_ICONS[channel?.kind] || Hash;

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white">
      <Navbar />
      <div className="pt-20 h-[100dvh] flex flex-col">
        <div className="px-4 py-2 flex items-center gap-3 text-sm shrink-0">
          <Link to={`/bot-dashboard/${botId}`} className="inline-flex items-center gap-1.5 text-white/60 hover:text-white"><ArrowLeft className="w-4 h-4" />Tableau de bord du bot</Link>
          <span className="text-white/30">·</span>
          <span className="inline-flex items-center gap-1.5 text-white/80"><Bot className="w-4 h-4 text-indigo-300" />Console Discord de {home.bot.name}</span>
        </div>
        <div className="flex-1 min-h-0 flex mx-2 mb-2 rounded-xl overflow-hidden border border-white/10 bg-[#313338]" data-testid="bot-console">
          <div className={`${mobilePane === "nav" ? "flex" : "hidden"} md:flex min-h-0`}>
            <GuildRail guilds={home.guilds} current={guildId} dmsActive={isDMs} onPick={pickGuild} onMenu={openMenu("guild")} onAdd={() => setDialog({ type: "addbot" })} />
            {isDMs ? (
              <aside className="w-60 shrink-0 bg-[#2b2d31] flex flex-col min-h-0">
                <div className="h-12 px-4 flex items-center font-semibold border-b border-black/30">Messages privés</div>
                <DMList dms={home.dms} current={channel?.id} onOpen={openDM} onClose={closeDM}
                  onPick={(d) => { setChannel({ id: d.channel_id, name: d.user.name, kind: "dm", writable: true, guildId: "@dms", user: d.user }); setMobilePane("chat"); }} />
                <BotFooter bot={home.bot} onLog={() => setShowLog(true)} onProfile={() => setDialog({ type: "profile" })} />
              </aside>
            ) : (
              <ChannelList title={currentGuild?.name || "Serveur"} channels={guild?.channels || []} current={channel?.id}
                onPick={(c) => { setChannel({ ...c, guildId }); setMobilePane("chat"); }}
                onServerMenu={(e) => currentGuild && openMenu("guild")(e, currentGuild)} onChannelMenu={openMenu("channel")}
                onInvite={currentGuild ? () => setDialog({ type: "invite" }) : null}
                footer={<BotFooter bot={home.bot} onLog={() => setShowLog(true)} onProfile={() => setDialog({ type: "profile" })} />} />
            )}
          </div>

          <section className={`${mobilePane === "chat" ? "flex" : "hidden"} md:flex flex-1 min-w-0 flex-col`}>
            <header className="h-12 shrink-0 px-4 flex items-center gap-2 border-b border-black/30">
              <button type="button" className="md:hidden p-1 -ml-1 text-white/70" onClick={() => setMobilePane("nav")} aria-label="Salons"><ArrowLeft className="w-5 h-5" /></button>
              {channel ? <><ChatIcon className="w-5 h-5 text-white/50" /><span className="font-semibold truncate">{channel.name}</span>
                {info?.channel?.topic && <span className="hidden sm:block text-xs text-white/50 truncate border-l border-white/10 pl-2">{info.channel.topic}</span>}</>
                : <span className="text-white/50">{isDMs ? "Choisis ou ouvre un MP" : "Choisis un salon"}</span>}
              {!isDMs && (
                <button type="button" title="Membres" onClick={() => setShowMembers((v) => !v)}
                  className={`ml-auto hidden lg:block p-1.5 rounded ${showMembers ? "text-white" : "text-white/50"} hover:bg-white/10`}><Users className="w-5 h-5" /></button>
              )}
            </header>
            {info?.content_hidden && (
              <div className="mx-4 mt-2 rounded bg-amber-500/10 border border-amber-500/30 px-3 py-2 text-xs text-amber-100" data-testid="console-intent-hint">
                Le texte des messages des membres est vide : active « Message Content Intent » sur le Discord Developer Portal (Bot › Privileged Gateway Intents).
              </div>
            )}
            <div className="flex-1 min-h-0 overflow-y-auto pb-4" ref={scroller}
              onScroll={(e) => { const el = e.currentTarget; atBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80; }}>
              {channel && (
                <div className="px-4 pt-6 pb-2">
                  {hasMore ? (
                    <button type="button" onClick={loadOlder} disabled={loadingMore} className="text-xs text-sky-400 hover:underline" data-testid="console-older">
                      {loadingMore ? "Chargement…" : "Charger les messages précédents"}
                    </button>
                  ) : messages.length > 0 && (
                    <div><div className="w-14 h-14 rounded-full bg-white/10 flex items-center justify-center mb-2"><ChatIcon className="w-7 h-7" /></div>
                      <div className="text-2xl font-bold">{channel.kind === "dm" ? channel.name : `Bienvenue dans #${channel.name}`}</div>
                      <div className="text-sm text-white/50">C'est le début {channel.kind === "dm" ? "de ta conversation en MP avec ce membre" : "de ce salon"}.</div></div>
                  )}
                </div>
              )}
              {messages.map((m, i) => {
                const prev = messages[i - 1];
                const grouped = !!prev && !m.reply && prev.author.id === m.author.id && new Date(m.timestamp) - new Date(prev.timestamp) < 5 * 60 * 1000;
                return <Message key={m.id} m={m} grouped={grouped} ctx={ctx} onReply={setReplyTo} onReact={react} onEdit={edit} onDelete={remove}
                  editing={editing === m.id} setEditing={setEditing} onMenu={openMenu("message")} onAuthorMenu={openMenu("member")} />;
              })}
              {channel && !messages.length && info && <p className="px-4 text-sm text-white/40">Aucun message ici pour l'instant.</p>}
            </div>
            {channel && <Composer channel={channel} replyTo={replyTo} setReplyTo={setReplyTo} onSend={send} sending={sending} botId={botId} insert={insert} />}
          </section>

          {!isDMs && showMembers && <Members data={members} roles={guild?.roles || {}} onMenu={openMenu("member")} />}
        </div>
      </div>
      {showLog && <LogModal botId={botId} onClose={closeLog} />}
      <ContextMenu menu={menu} items={menuItems()} onClose={closeMenu} />
      {dialog?.type === "server" && currentGuild && (
        <ServerSettingsModal api={api} botId={botId} guild={currentGuild} data={guild} tab={dialog.tab} onClose={closeDialog}
          onChanged={async (r) => { if (r?.guilds) await loadHome(); await refreshGuild(true); }} />
      )}
      {dialog?.type === "profile" && (
        <ProfileModal api={api} botId={botId} bot={home.bot} guild={isDMs ? null : currentGuild} nick={guild?.me?.nick} onClose={closeDialog}
          onSaved={async () => { await loadHome(); await refreshGuild(true); }} />
      )}
      {dialog?.type === "channel" && (
        <ChannelModal api={api} botId={botId} guildId={guildId} channel={dialog.channel} kind={dialog.kind} parentId={dialog.parentId || ""}
          categories={(guild?.channels || []).filter((c) => c.kind === "category")} onClose={closeDialog} onDone={() => refreshGuild()} />
      )}
      {dialog?.type === "nick" && <NickModal api={api} botId={botId} guildId={guildId} member={dialog.member} onClose={closeDialog} onDone={() => refreshGuild(true)} />}
      {dialog?.type === "sanction" && (
        <SanctionModal api={api} botId={botId} guildId={guildId} member={dialog.member} kind={dialog.kind} onClose={closeDialog} onDone={() => refreshGuild(true)} />
      )}
      {dialog?.type === "invite" && currentGuild && (
        <InviteModal api={api} botId={botId} guild={currentGuild} channels={guild?.channels || []} channelId={dialog.channelId || ""}
          canInvite={guild?.me?.perms?.invite !== false} onClose={closeDialog} />
      )}
      {dialog?.type === "addbot" && (
        <AddBotModal api={api} botId={botId} botName={home?.bot?.name} onClose={closeDialog} onRefresh={() => loadHome().catch(() => {})} />
      )}
      {dialog?.type === "confirm" && <ConfirmModal {...dialog} onConfirm={dialog.run} onClose={closeDialog} />}
    </div>
  );
}
